"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";

// Global custom events for programmatic trigger
const START_EVENT = "targetqa:navigation-start";
const STOP_EVENT = "targetqa:navigation-end";

/**
 * Programmatically start the professional navigation loader.
 * Useful for asynchronous buttons or long actions (e.g. checkout, login).
 */
export function startNavigationProgress() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(START_EVENT));
  }
}

/**
 * Programmatically complete the navigation loader.
 */
export function stopNavigationProgress() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(STOP_EVENT));
  }
}

interface NavigationProgressContextValue {
  start: () => void;
  done: () => void;
  isLoading: boolean;
}

const NavigationProgressContext = createContext<NavigationProgressContextValue>({
  start: () => {},
  done: () => {},
  isLoading: false,
});

export function useNavigationProgress() {
  return useContext(NavigationProgressContext);
}

/**
 * Determines whether a clicked anchor represents an internal Next.js route change
 * that requires loading a different page or query string.
 */
function isInternalPageNavigation(anchor: HTMLAnchorElement): boolean {
  if (anchor.target && anchor.target !== "_self") return false;
  if (anchor.hasAttribute("download")) return false;

  const rel = anchor.getAttribute("rel") || "";
  if (rel.includes("external")) return false;

  const rawHref = anchor.getAttribute("href");
  if (!rawHref) return false;

  // Ignore non-navigation schemes
  if (
    rawHref.startsWith("#") ||
    rawHref.startsWith("mailto:") ||
    rawHref.startsWith("tel:") ||
    rawHref.startsWith("sms:") ||
    rawHref.startsWith("javascript:")
  ) {
    return false;
  }

  try {
    const dest = new URL(anchor.href, window.location.href);

    // Only same-origin destinations
    if (dest.origin !== window.location.origin) return false;

    // Ignore clicks that point to the exact same pathname + query
    if (
      dest.pathname === window.location.pathname &&
      dest.search === window.location.search
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

type LoaderStatus = "idle" | "loading" | "finishing";

export function NavigationProgress({ children }: { children?: ReactNode }) {
  const pathname = usePathname();

  const [status, setStatus] = useState<LoaderStatus>("idle");
  const [showPill, setShowPill] = useState(false);
  const [isFading, setIsFading] = useState(false);

  const activeRef = useRef(false);
  const debounceTimerRef = useRef<number | null>(null);
  const pillTimerRef = useRef<number | null>(null);
  const safetyTimerRef = useRef<number | null>(null);
  const finishTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pillTimerRef.current !== null) {
      window.clearTimeout(pillTimerRef.current);
      pillTimerRef.current = null;
    }
    if (safetyTimerRef.current !== null) {
      window.clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    if (finishTimerRef.current !== null) {
      window.clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
  }, []);

  const done = useCallback(() => {
    if (!activeRef.current) return;
    activeRef.current = false;

    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pillTimerRef.current !== null) {
      window.clearTimeout(pillTimerRef.current);
      pillTimerRef.current = null;
    }
    if (safetyTimerRef.current !== null) {
      window.clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }

    setStatus("finishing");
    setShowPill(false);

    finishTimerRef.current = window.setTimeout(() => {
      setIsFading(true);
      finishTimerRef.current = window.setTimeout(() => {
        setStatus("idle");
        setIsFading(false);
      }, 70);
    }, 60);
  }, []);

  const start = useCallback(() => {
    if (activeRef.current) return;

    clearTimers();
    activeRef.current = true;
    setIsFading(false);

    // Debounce: do not render anything if navigation completes within 180ms
    debounceTimerRef.current = window.setTimeout(() => {
      if (!activeRef.current) return;

      setStatus("loading");

      // Show floating brand loader only if navigation genuinely takes > 650ms
      pillTimerRef.current = window.setTimeout(() => {
        if (activeRef.current) {
          setShowPill(true);
        }
      }, 470);
    }, 180);

    // Safety timeout: auto-clear after 8 seconds
    safetyTimerRef.current = window.setTimeout(done, 8000);
  }, [clearTimers, done]);

  // Complete loader when route pathname changes
  useEffect(() => {
    done();
  }, [pathname, done]);

  // Global link click and history listeners
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.defaultPrevented) return;

      const target = e.target as HTMLElement | null;
      const anchor = target?.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;

      if (isInternalPageNavigation(anchor)) {
        start();
      }
    };

    const handlePopState = () => {
      start();
    };

    const handleCustomStart = () => start();
    const handleCustomStop = () => done();

    document.addEventListener("click", handleClick, { capture: true, passive: true });
    window.addEventListener("popstate", handlePopState, { passive: true });
    window.addEventListener(START_EVENT, handleCustomStart);
    window.addEventListener(STOP_EVENT, handleCustomStop);

    return () => {
      document.removeEventListener("click", handleClick, { capture: true } as EventListenerOptions);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener(START_EVENT, handleCustomStart);
      window.removeEventListener(STOP_EVENT, handleCustomStop);
      clearTimers();
    };
  }, [start, done, clearTimers]);

  return (
    <>
      {status !== "idle" && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[9999999] select-none"
        >
          {/* 1. GPU-Accelerated Sleek Top Progress Bar */}
          <div
            className="fixed top-0 left-0 right-0 h-[3px] bg-transparent overflow-hidden"
            style={{
              opacity: isFading ? 0 : 1,
              transition: "opacity 80ms ease",
            }}
          >
            <div
              className="h-full w-full"
              style={{
                background: "linear-gradient(90deg, #0a7653 0%, #10b981 50%, #c0ffa5 100%)",
                transform: status === "finishing" ? "translateX(0)" : "translateX(-20%)",
                transition:
                  status === "finishing"
                    ? "transform 80ms ease-out"
                    : "transform 3s cubic-bezier(0.08, 0.82, 0.17, 1)",
                willChange: "transform",
                boxShadow: "0 0 10px 1px #c0ffa5",
              }}
            />
          </div>

          {/* 2. Professional Floating Brand Loader Pill (shown if navigation > 400ms) */}
          <div
            className="fixed top-4 left-1/2 -translate-x-1/2 flex items-center gap-2.5 rounded-full px-4 py-2 border border-black/10 bg-white/95 dark:bg-[#101512]/95 backdrop-blur-md shadow-[0_12px_32px_rgba(21,35,27,0.14)]"
            style={{
              opacity: showPill && !isFading ? 1 : 0,
              transform: showPill && !isFading
                ? "translate(-50%, 0) scale(1)"
                : "translate(-50%, -10px) scale(0.96)",
              transition: "opacity 200ms ease, transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* Animated Brand Dual-Ring Spinner */}
            <div className="relative flex h-4 w-4 shrink-0 items-center justify-center">
              <div className="absolute inset-0 rounded-full border-[2px] border-emerald-950/10 dark:border-white/10" />
              <div className="absolute inset-0 animate-spin rounded-full border-[2px] border-transparent border-t-[#0a7653] border-r-[#c0ffa5]" />
            </div>

            {/* Status text with subtle pulsing dot */}
            <div className="flex items-center gap-1.5 text-xs font-semibold tracking-[-0.01em] text-[#1b1b1b] dark:text-white">
              <span>Loading</span>
              <span className="flex items-center gap-0.5">
                <span className="h-1 w-1 rounded-full bg-[#0a7653] animate-pulse" />
                <span
                  className="h-1 w-1 rounded-full bg-[#10b981] animate-pulse"
                  style={{ animationDelay: "150ms" }}
                />
                <span
                  className="h-1 w-1 rounded-full bg-[#c0ffa5] animate-pulse"
                  style={{ animationDelay: "300ms" }}
                />
              </span>
            </div>
          </div>
        </div>
      )}
      {children}
    </>
  );
}
